import React, { useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import Select from "react-select";
import { Alert, Col, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import ControlService from "src/modules/data-protection/apis/ControlService";
import FilteringComponent from "./components/filtering-component";
import ControlsOverviewWidgets from "./components/ControlsOverviewWidgets";
import TasksActionsWidgets from "./components/TasksActionsWidgets";
import ComplianceReadinessSection from "./components/ComplianceReadinessSection";
import MappingQualitySection from "./components/MappingQualitySection";
import FrameworkMappingMatrixSection from "./components/FrameworkMappingMatrixSection";
import LegalEntityComparisonSection from "./components/legal-entity-comparision/LegalEntityComparisonSection";
import ControlHealthSection from "./components/ControlHealthSection";

const frameworkLabelMap = {
  ISO27001: "ISO 27001",
  TISAX: "TISAX",
  C5_2020: "C5-2020",
  DORA: "DORA",
  NIS2: "NIS2",
  ISO42001: "ISO/IEC 42001"
};

const ControlsDashboard = ({
  t,
  ControlsFrameworksOptions = [],
  controlType,
  setControlType,
}) => {
  const dashboardParams = useMemo(() => {
    const type = controlType?.value || "ISO27001";
    const params = { type };

    if (type === "ISO42001") {
      params.mapping_target_framework = "ISO27001";
    }

    return params;
  }, [controlType?.value]);

  const summaryQuery = useQuery({
    queryKey: ["data-protection-controls-dashboard-summary", dashboardParams.type],
    queryFn: async () => {
      const service = ControlService.getInstance();
      return await service.dashboardSummary(dashboardParams);
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: () => {
      toast(t("An error occurred while fetching dashboard data."), {
        type: "error",
      });
    },
  });

  const secondaryQueriesEnabled = summaryQuery.isSuccess;

  const controlsQuery = useQuery({
    queryKey: ["data-protection-controls-dashboard-controls", dashboardParams.type],
    queryFn: async () => {
      const service = ControlService.getInstance();
      return await service.dashboardControls(dashboardParams);
    },
    enabled: secondaryQueriesEnabled,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: () => {
      toast(t("An error occurred while fetching control health data."), {
        type: "error",
      });
    },
  });

  const dashboardData = useMemo(() => {
    if (!summaryQuery.data) {
      return undefined;
    }

    return {
      ...summaryQuery.data,
      ...controlsQuery.data,
    };
  }, [summaryQuery.data, controlsQuery.data]);

  const isSummaryLoading = summaryQuery.isFetching;
  const isSummaryError = summaryQuery.isError;

  return (
    <div>
      <Row className="mb-3">
        <Col sm="12" md="6" lg="3" className="mb-3">
          <Select
            name="type"
            classNamePrefix="select2-selection"
            options={ControlsFrameworksOptions}
            value={controlType}
            placeholder={t("Frameworks")}
            onChange={setControlType}
          />
        </Col>
        {/* <Col sm="12" md="6" lg="9" className="mb-3 d-flex justify-content-md-end">
          <FilteringComponent />
        </Col> */}
      </Row>

      {isSummaryLoading ? (
        <div className="text-center py-5">
          <Spinner size="sm" className="me-2" />
          {t("Loading")}...
        </div>
      ) : null}

      {isSummaryError ? (
        <Alert color="danger">{t("Unable to load data at this time. Please refresh the page or try again later.")}</Alert>
      ) : null}

      {!isSummaryLoading && !isSummaryError && dashboardData ? (
        <>
          <h5 className="mb-3">{t("Controls Overview")}</h5>
          <ControlsOverviewWidgets
            dashboardData={dashboardData}
            selectedFramework={dashboardParams.type}
          />

          <h5 className="mb-3">{t("Tasks & Actions")}</h5>
          <TasksActionsWidgets dashboardData={dashboardData} />
          <ComplianceReadinessSection
            t={t}
            dashboardData={dashboardData}
            frameworkLabelMap={frameworkLabelMap}
            selectedFramework={dashboardParams.type}
          />
          <Row className="mb-1 align-items-stretch">
            <MappingQualitySection t={t} dashboardData={dashboardData} />
            <FrameworkMappingMatrixSection
              t={t}
              dashboardData={dashboardData}
              frameworkLabelMap={frameworkLabelMap}
            />
          </Row>

          <LegalEntityComparisonSection
            t={t}
            frameworkType={dashboardParams.type}
            queryEnabled={secondaryQueriesEnabled}
          />

          <ControlHealthSection
            t={t}
            dashboardData={dashboardData}
            frameworkLabelMap={frameworkLabelMap}
            selectedFramework={dashboardParams.type}
            isLoading={controlsQuery.isFetching}
            isError={controlsQuery.isError}
          />
        </>
      ) : null}
    </div>
  );
};

export default withNamespaces()(ControlsDashboard);
